import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminApi, apiError, formatDateTime, formatHuf, FORMAT_LABEL } from "@/admin/adminApi";
import SessionDialog from "@/admin/components/SessionDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { btnPrimary, card, Pill } from "@/admin/ui";

function SessionRow({ s, onEdit, onDelete, onToggle }) {
  return (
    <li data-testid={`session-row-${s.id}`} className="py-4 grid md:grid-cols-[1.6fr_1fr_1fr_auto] gap-3 md:items-center">
      <div>
        <p className="font-body text-[14.5px] text-graphite">{formatDateTime(s.starts_at)}</p>
        <p className="font-body text-[12.5px] text-graphite-soft">
          {s.duration_minutes} perc · {FORMAT_LABEL[s.format]}{s.location ? ` · ${s.location}` : ""} · {formatHuf(s.price_huf)} / fő
        </p>
      </div>
      <div className="flex items-center gap-2.5">
        <span className="font-body text-[13.5px]" data-testid={`session-seats-${s.id}`}>{s.registered} / {s.capacity} fő</span>
        {s.free === 0 ? <Pill tone="gold">Betelt</Pill> : <Pill tone="petrol">{s.free} szabad</Pill>}
        {s.waitlist > 0 && <Pill tone={s.seat_freed_alert ? "gold" : "muted"} testId={`session-waitlist-${s.id}`}>Várólista: {s.waitlist}</Pill>}
      </div>
      <label className="flex items-center gap-2.5 cursor-pointer font-body text-[13px]" data-testid={`session-toggle-${s.id}`}>
        <input type="checkbox" checked={s.registration_open} onChange={() => onToggle(s)} className="accent-gold" />
        {s.registration_open ? "Jelentkezés nyitva" : "Jelentkezés zárva"}
      </label>
      <div className="flex gap-1">
        <button type="button" aria-label="Szerkesztés" onClick={() => onEdit(s)} data-testid={`session-edit-${s.id}`} className="p-2 text-graphite-soft hover:text-gold"><Pencil className="w-4 h-4" /></button>
        <button type="button" aria-label="Törlés" onClick={() => onDelete(s)} data-testid={`session-delete-${s.id}`} className="p-2 text-graphite-soft hover:text-[#9A3B2E]"><Trash2 className="w-4 h-4" /></button>
      </div>
    </li>
  );
}

const pick = ({ starts_at, duration_minutes, format, location, capacity, price_huf, registration_open }) =>
  ({ starts_at, duration_minutes, format, location, capacity, price_huf, registration_open });

export default function SessionsPanel({ programId, sessions, setSessions }) {
  const [dialog, setDialog] = useState({ open: false, session: null });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const replace = (updated) => setSessions((list) => list.map((x) => (x.id === updated.id ? updated : x)));
  const sortList = (list) => [...list].sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  const save = async (payload) => {
    setSaving(true);
    try {
      if (dialog.session) {
        const { data } = await adminApi.put(`/admin/sessions/${dialog.session.id}`, payload);
        setSessions((list) => sortList(list.map((x) => (x.id === data.id ? data : x))));
      } else {
        const { data } = await adminApi.post(`/admin/programs/${programId}/sessions`, payload);
        setSessions((list) => sortList([...list, data]));
      }
      toast.success("Az időpont elmentve.");
      setDialog({ open: false, session: null });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s) => {
    try {
      const { data } = await adminApi.put(`/admin/sessions/${s.id}`, { ...pick(s), registration_open: !s.registration_open });
      replace(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (s) => {
    try {
      await adminApi.delete(`/admin/sessions/${s.id}`);
      setSessions((list) => list.filter((x) => x.id !== s.id));
      toast.success("Az időpont törölve.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToDelete(null);
    }
  };

  return (
    <section className={`${card} px-6 md:px-8 py-7`} data-testid="sessions-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <h2 className="font-serif-display text-[26px] text-graphite">Időpontok</h2>
        <button type="button" onClick={() => setDialog({ open: true, session: null })} data-testid="session-add-btn" className={btnPrimary}>
          <Plus className="w-4 h-4" /> Új időpont
        </button>
      </div>
      {sessions.length === 0 ? (
        <p data-testid="sessions-empty" className="font-body text-[14px] text-graphite-soft py-3">Ehhez a programhoz még nincs időpont.</p>
      ) : (
        <ul className="divide-y divide-[#E6E1D8]">
          {sessions.map((s) => (
            <SessionRow key={s.id} s={s} onEdit={(x) => setDialog({ open: true, session: x })} onDelete={setToDelete} onToggle={toggle} />
          ))}
        </ul>
      )}
      <SessionDialog open={dialog.open} session={dialog.session} saving={saving} onSave={save} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none bg-warm" data-testid="session-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-[26px] font-normal">Időpont törlése</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Biztosan törlöd ezt az időpontot{toDelete ? ` (${formatDateTime(toDelete.starts_at)})` : ""}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="session-delete-cancel">Mégse</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[#9A3B2E] hover:bg-[#7f3026]" onClick={() => remove(toDelete)} data-testid="session-delete-confirm">Törlés</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
